import { gql } from '@apollo/client';

export const GET_ALL_VISITORS_GROUPS = gql`
  query GetAllVisitorGroups($filter: VisitorGroupsFilter) {
    getVisitorGroupsForTenant(filter: $filter) {
      id
      name
      type
      maxNumberOfMembers
      isQrCodeVisitInviterGroup
      memberIds
      members {
        id
        name
        email
        identityProvider
        expirationDate
        visitorGroupMembership {
          visitorGroup {
            id
            type
            name
          }
          role
          expirationDate
        }
        mobileDevices {
          id
          isRegistered
          registrationToken
          registrationDate
          role
        }
      }
      accessGrants {
        id
        isOfficeModeEnabled
        accessSchedule {
          ... on WeekSchedule {
            weekDays {
              dayName
              from
              to
            }
          }
        }
        locationIds
        doorIds
        locations {
          id
          name
          doors {
            id
          }
        }
        doors {
          id
          locations {
            id
            name
          }
        }
      }
    }
  }
`;

export const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS = gql`
  query GetAllVisitorGroupsWithoutNestedResolvers($filter: VisitorGroupsFilter) {
    getVisitorGroupsForTenant(filter: $filter) {
      id
      name
      type
      maxNumberOfMembers
      isQrCodeVisitInviterGroup
      memberIds
      accessGrants {
        id
        isOfficeModeEnabled
        accessSchedule {
          ... on WeekSchedule {
            weekDays {
              dayName
              from
              to
            }
          }
        }
        locationIds
        doorIds
      }
    }
  }
`;

export const GET_ALL_VISITORS_VISITOR_GROUPS = gql`
  query GetAllVisitorsVisitorGroups($filter: VisitorGroupsFilter) {
    getVisitorGroupsForTenant(filter: $filter) {
      id
      name
      type
      maxNumberOfMembers
      members {
        id
        email
      }
    }
  }
`;

export const GET_VISITOR_DETAILS = gql`
  query GetVisitorDetails($id: ID!) {
    getVisitorById(id: $id) {
      id
      name
      email
      mobileDevices {
        id
        isRegistered
        registrationToken
        role
      }
      identityProvider
      expirationDate
      isAdmin
      visitorGroupMembership {
        visitorGroup {
          id
          name
          type
        }
        role
        expirationDate
      }
    }
  }
`;

export const GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE = gql`
  query GetVisitorGroupsForTenantWithMembersAndRole($filter: VisitorGroupsFilter) {
    getVisitorGroupsForTenant(filter: $filter) {
      id
      name
      members {
        id
        name
        email
        mobileDevices {
          id
          isRegistered
          registrationToken
          registrationDate
          role
        }
        isEditable
        expirationDate
        visitorGroupMembership {
          visitorGroup {
            id
            type
          }
          role
          expirationDate
        }
        isAdmin
      }
      type
      externalRef
      maxNumberOfMembers
    }
  }
`;

export const GET_REST_API_KEYS = gql`
  query GetRestApiKeys {
    getRestApiKeys {
      apiKey
      createdAt
      description
      id
    }
  }
`;

export const GET_ALL_DOORS_FOR_DEVICE_GROUPS = gql`
  query GetAllDoorsForDeviceGroups {
    getDoorsForTenant {
      id
      name
      type
      hardwareIdentifier
    }
  }
`;

export const GET_ACCESS_CARDS = gql`
  query GetAccessCards {
    getNfcCards {
      uuid
      name
      updateRequired
      assignment {
        expiryDate
        expired
        blocked {
          since
          reason
        }
        visitor {
          id
          expirationDate
          email
          name
        }
        visitorName
      }
    }
  }
`;

export const GET_VISITOR_GROUPS_FOR_TENANT_WITH_SCHEDULES = gql`
  query GetVisitorGroupsForTenantWithSchedules {
    getVisitorGroupsForTenant {
      id
      name
      accessGrants {
        id
        accessSchedule {
          ... on WeekSchedule {
            weekDays {
              dayName
              from
              to
            }
          }
        }
      }
      type
    }
  }
`;

export const GET_DOORS_FOR_TENANT = gql`
  query GetDoorsForTenantWithLocationsV2($filter: DoorsFilter) {
    getDoorsForTenant(filter: $filter) {
      id
      name
      locationIds
      hardwareIdentifier
      batteryWarningLevel
      officeMode {
        ... on WeekSchedule {
          weekDays {
            dayName
            from
            to
          }
        }
      }
      type
    }
  }
`;

export const GET_ALL_VISITORS_FOR_TENANT = gql`
  query GetAllVisitorsForTenant {
    getVisitors {
      id
      name
      email
      mobileDevices {
        id
        isRegistered
        registrationToken
        registrationDate
        role
      }
      identityProvider
      expirationDate
      visitorGroupMembership {
        visitorGroup {
          id
          type
          name
        }
        role
        expirationDate
      }
      isAdmin
    }
  }
`;

export const GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS = gql`
  query GetAllVisitorsWithoutNestedResolvers {
    getVisitors {
      id
      name
      email
      mobileDevices {
        id
        isRegistered
        registrationToken
        registrationDate
        role
      }
      identityProvider
      expirationDate
      visitorGroupMembership {
        visitorGroup {
          id
        }
        role
        expirationDate
      }
      isAdmin
    }
  }
`;

export const GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS = gql`
  query GetVisitorGroupsWithoutNestedResolvers {
    getVisitorGroupsForTenant {
      id
      name
      type
    }
  }
`;

export const GET_QR_CODE_VISITS_FOR_TENANT = gql`
  query GetQrCodeVisitsForTenant {
    getQrCodeVisitsForTenant {
      id
      name
      datetime
      email
      status
      host {
        id
        name
        email
      }
    }
  }
`;

export const GET_ACCESS_EVENTS_FOR_TENANT = gql`
  query GetAccessEventsForTenantV2($nextToken: String, $filter: AccessEventsFilter) {
    getAccessEventsForTenant(nextToken: $nextToken, filter: $filter) {
      items {
        id
        createdAt
        visitor {
          id
          name
          email
        }
        door {
          id
          name
          type
        }
        action
        reason
      }
      nextToken
    }
  }
`;

export const GET_VISITORS_WITHOUT_NESTED_RESOLVERS = gql`
  query GetVisitorsWithoutNestedResolvers {
    getVisitors {
      id
      name
      email
      mobileDevices {
        id
        isRegistered
        registrationToken
        registrationDate
        role
      }
      identityProvider
      expirationDate
      visitorGroupMembership {
        visitorGroup {
          id
        }
        role
        expirationDate
      }
      isAdmin
    }
  }
`;

export const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS = gql`
  query GetLocationsWithoutNestedResolvers {
    getLocationsForTenant {
      id
      name
      doors {
        id
      }
    }
  }
`;

export const GET_DOORS_WITHOUT_NESTED_RESOLVERS = gql`
  query GetDoorsWithoutNestedResolvers {
    getDoorsForTenant {
      id
      name
      hardwareIdentifier
    }
  }
`;

export const GET_DOORS_FOR_ACCESS_EVENTS_FILTERS = gql`
  query GetDoorsForTenantForAccessEvents {
    getDoorsForTenant {
      id
      name
    }
  }
`;

export const GET_VISITORS_ACCESS_EVENTS_FILTERS = gql`
  query GetAllVisitorsForTenantForAccessEvents {
    getVisitors {
      id
      name
      email
    }
  }
`;

export const GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS = gql`
  query GetAllVisitorsCardsWithoutNestedResolvers {
    getNfcCards {
      uuid
      name
      updateRequired
      assignment {
        expiryDate
        expired
        blocked {
          since
          reason
        }
        visitor {
          id
        }
        visitorName
      }
    }
  }
`;

export const GET_READER_FIRMWARE_UPDATE = gql`
  query GetReaderFirmwareUpdate($doorId: ID!) {
    cloudController {
      getReaderFirmwareUpdate(doorId: $doorId) {
        doorId
        status
        percentage
      }
    }
  }
`;
